import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Mirror of backend/utils/concessions.js key rules
export const SHARED_KEY = 'shared';
export const SHARED_TYPE = { key: SHARED_KEY, displayName: 'Class Concessions', isShared: true };

export function slugifyTypeKey(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function prettifyKey(key) {
  const clean = String(key || '').replace(/-/g, ' ').trim();
  return clean.charAt(0).toUpperCase() + clean.slice(1);
}

/**
 * Visibility rule: a typed class is only visible to users carrying that mark.
 * Untyped (shared) classes are visible to everyone - this keeps existing
 * members seeing exactly what they saw before.
 */
export function canSeeClass(cls, user) {
  if (!cls) return false;
  if (!cls.concessionType) return true;
  const marks = Array.isArray(user?.concessionTypes) ? user.concessionTypes : [];
  return marks.includes(cls.concessionType);
}

/**
 * Fetch the concession-type registry (any authenticated user).
 * Returns { types, loading, refetch } where the implicit shared entry comes first.
 */
export function useConcessionTypes(enabled = true) {
  const [types, setTypes] = useState(enabled ? [SHARED_TYPE] : []);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!enabled) return Promise.resolve();
    setLoading(true);
    return axios.get('/api/concession-types')
      .then((res) => {
        if (res.data?.types) {
          setTypes(res.data.types);
        }
      })
      .catch(() => {
        // Fall back to just the shared entry - non-admins get a 40x here
      })
      .finally(() => {
        setLoading(false);
      });
  }, [enabled]);

  useEffect(() => {
    load();
  }, [load]);

  return { types, loading, refetch: load };
}

/**
 * Resolve a pool display name from the registry list, falling back to a
 * prettified slug for unknown keys.
 */
export function poolDisplayName(types, key) {
  if (!key || key === SHARED_KEY) return SHARED_TYPE.displayName;
  const found = (types || []).find(t => t.key === key);
  return found ? found.displayName : prettifyKey(key);
}

/**
 * Read the user's balance in the pool a class draws from.
 * Shared classes read the legacy `concessions` field; typed classes read
 * `concessionBalances[key]`.
 */
export function resolvePoolBalance(user, classTypeKey) {
  if (!user) return 0;
  if (!classTypeKey) return user.concessions || 0;
  const balances = user.concessionBalances || {};
  return typeof balances[classTypeKey] === 'number' ? balances[classTypeKey] : 0;
}
